/// <reference lib="dom" />
import type { DecorationSettings, EdgeArtMode, FoliageKind, FoliagePaint, GrassLines, GrassPaint, LevelDoc, PaletteEntry, ShadingSettings, SpikeLines, TerrainShape, TileLayer, Vec2 } from '../model/format.ts';
import { loadAsset, saveAsset } from '../data/remoteStore.ts';
import { loadCustomTiles, onCustomTilesChanged } from '../data/customTiles.ts';
import { cellKey, compileStyle, generateContainer, learnedChoices, linkedChoices, probeContainer, type CompiledStyle, type MissingTile } from './runtime.ts';
import { classifyCells, createGrid, paintRings, ringsBounds, shapeKindAllowed } from './raster.ts';
import { FULL, SHAPES } from './shapes.ts';
import { BURIED, buriedNeighbours, caseKey, exactCase, generalCase, toneCase, type CaseLayer } from './cases.ts';
import { canonicalSprite, variantSprites } from './variants.ts';
import { NONE, type StyleOverride, type TerrainData, type TerrainStyleData } from './types.ts';

export type { SpikeCorner, StyleOverride, TerrainData, TerrainStyleData } from './types.ts';
export type { MissingTile } from './runtime.ts';

export interface TerrainContainer {
  layers: TileLayer[];
  terrains: TerrainShape[];
  grassPaint?: GrassPaint;
  grassLines?: GrassLines;
  spikeLines?: SpikeLines;
  decoPaint?: FoliagePaint;
}

export interface TerrainStyleInfo {
  id: string;
  name: string;
  tex: string;
  variants: string[];
  directions: { s45: boolean; s21: boolean; s12: boolean };
  defaultShading: ShadingSettings;
  defaultDecoration: DecorationSettings;
  /** number of decorations of each kind the style has */
  foliage: Partial<Record<FoliageKind, number>>;
}

let data: TerrainData | null = null;
const overrides = new Map<string, StyleOverride>();
const compiled = new Map<string, CompiledStyle>();
let version = 0;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

function changed() {
  compiled.clear();
  version++;
  for (const l of listeners) l();
}

onCustomTilesChanged(changed);

/** Increments whenever style data or overrides change (include it in terrain output cache keys). */
export function terrainDataVersion(): number {
  return version;
}

/** Subscribe to style data changes (load finished, overrides edited). Returns an unsubscribe function. */
export function onTerrainStylesChanged(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Fetch terrain.json once and merge the user overrides stored as 'terrain-style' assets. */
export function loadTerrainStyles(base = '/game-data'): Promise<void> {
  if (loading) return loading;
  loading = (async () => {
    const r = await fetch(`${base}/terrain.json`);
    if (!r.ok) throw new Error(`terrain.json: ${r.status}`);
    const d = (await r.json()) as TerrainData;
    await Promise.all(
      d.styles.map(async (s) => {
        try {
          const ov = await loadAsset<StyleOverride>('terrain-style', s.id);
          if (ov) overrides.set(s.id, ov);
        } catch {
          /* no override */
        }
      }),
    );
    await loadCustomTiles(d.styles.map((s) => s.id));
    data = d;
    changed();
  })();
  loading.catch(() => {
    loading = null;
  });
  return loading;
}

/** Re-fetch style overrides and custom sheets so a lobby join sees tiles drawn after this tab loaded. */
export async function reloadTerrainAssets(): Promise<void> {
  if (!data) {
    await loadTerrainStyles();
    return;
  }
  await Promise.all(
    data.styles.map(async (s) => {
      try {
        const ov = await loadAsset<StyleOverride>('terrain-style', s.id);
        if (ov) overrides.set(s.id, ov);
        else overrides.delete(s.id);
      } catch {
        /* keep the copy this tab already has */
      }
    }),
  );
  await loadCustomTiles(data.styles.map((s) => s.id));
  changed();
}

/** Install style data directly (node tools / tests). */
export function setTerrainData(d: TerrainData, ovs?: Record<string, StyleOverride>) {
  data = d;
  overrides.clear();
  if (ovs) for (const [k, v] of Object.entries(ovs)) overrides.set(k, v);
  changed();
}

export function terrainData(): TerrainData | null {
  return data;
}

export function styleData(id: string): TerrainStyleData | null {
  return data?.styles.find((s) => s.id === id) ?? null;
}

export function styleOverride(id: string): StyleOverride | null {
  return overrides.get(id) ?? null;
}

/** Apply (and keep) an override for a style; generation picks it up immediately. Pass null to reset. */
export function setStyleOverride(id: string, ov: StyleOverride | null) {
  if (ov) overrides.set(id, ov);
  else overrides.delete(id);
  changed();
}

export function compiledStyle(id: string): CompiledStyle | null {
  let c = compiled.get(id);
  if (c) return c;
  const d = styleData(id);
  if (!d) return null;
  c = compileStyle(d, overrides.get(id));
  compiled.set(id, c);
  return c;
}

/** Style references may carry a variant as "style:variant". */
function parseStyle(style: string, variant?: string): { id: string; variant?: string } {
  const i = style.indexOf(':');
  if (i < 0) return { id: style, variant };
  return { id: style.slice(0, i), variant: variant ?? style.slice(i + 1) };
}

export function terrainStyles(): TerrainStyleInfo[] {
  if (!data) return [];
  return data.styles.map((s) => {
    const c = compiledStyle(s.id)!;
    const ov = overrides.get(s.id);
    return {
      id: s.id,
      name: ov?.name ?? s.name,
      tex: s.tex,
      variants: s.variants.map((v) => v.name),
      directions: { ...c.directions },
      defaultShading: { ...c.defaultShading },
      defaultDecoration: { ...c.defaultDecoration },
      foliage: c.stamps.reduce<Partial<Record<FoliageKind, number>>>((m, st) => ((m[st.kind] = (m[st.kind] ?? 0) + 1), m), {}),
    };
  });
}

function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a;
}

/** Edge directions a style can build: axis-aligned always, 1:1 / 2:1 / 1:2 (any multiple) when supported. */
export function allowedEdge(styleId: string, dx: number, dy: number): boolean {
  if (!dx && !dy) return false;
  if (!dx || !dy) return true;
  const g = gcd(dx, dy);
  const ax = Math.abs(dx / g), ay = Math.abs(dy / g);
  const st = compiledStyle(parseStyle(styleId).id);
  const dirs = st?.directions ?? { s45: true, s21: false, s12: false };
  if (ax === 1 && ay === 1) return dirs.s45;
  if (ax === 2 && ay === 1) return dirs.s21;
  if (ax === 1 && ay === 2) return dirs.s12;
  return false;
}

/** Problems of a single shape for UI markers (unsupported edges and cell geometry). */
export function validateShape(styleId: string, rings: Vec2[][]): { x: number; y: number; message: string }[] {
  const out: { x: number; y: number; message: string }[] = [];
  const { id } = parseStyle(styleId);
  const st = compiledStyle(id);
  const name = st?.data.name ?? id;
  for (const ring of rings) {
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i], b = ring[(i + 1) % ring.length];
      const dx = b[0] - a[0], dy = b[1] - a[1];
      if (!dx && !dy) continue;
      if (!allowedEdge(id, dx, dy)) {
        const ok = [st?.directions.s45 && '1:1', st?.directions.s21 && '2:1', st?.directions.s12 && '1:2'].filter(Boolean).join(', ');
        out.push({ x: (a[0] + b[0]) / 2, y: (a[1] + b[1]) / 2, message: `Edge ${dx},${dy} not supported by ${name} (straight${ok ? ', ' + ok : ''} only)` });
      }
    }
  }
  if (!rings.length || rings[0].length < 3) return out;
  const [x0, y0, x1, y1] = ringsBounds(rings);
  const w = Math.ceil(x1) - Math.floor(x0) + 2, h = Math.ceil(y1) - Math.floor(y0) + 2;
  if (w * h > 2_000_000) return out;
  const S = data?.samples ?? 4;
  const g = createGrid(Math.floor(x0) - 1, Math.floor(y0) - 1, w, h, S);
  paintRings(g, rings, 0);
  const dirs = st?.directions ?? { s45: true, s21: true, s12: true };
  const cls = classifyCells(g, (o) => o === 0, (sid) => shapeKindAllowed(sid, dirs));
  for (const ci of cls.bad.slice(0, 200)) {
    const sh = cls.shape[ci];
    out.push({
      x: g.x0 + (ci % w) + 0.5,
      y: g.y0 + Math.floor(ci / w) + 0.5,
      message: SHAPES[sh].kind === 'full' || SHAPES[sh].kind === 'empty' ? 'Cell has unsupported geometry (edges meet inside the cell)' : `Cell has unsupported geometry (approximated as ${SHAPES[sh].kind})`,
    });
  }
  return out;
}

/** Icon sprite for the style picker. */
export function previewSprite(styleId: string): PaletteEntry | null {
  const { id, variant } = parseStyle(styleId);
  const st = compiledStyle(id);
  if (!st) return null;
  const si = st.data.preview;
  const vi = variant ? st.data.variants.findIndex((v) => v.name === variant) : -1;
  return (vi > 0 ? variantSprites(st, vi)[si] : null) ?? st.sprites[si] ?? null;
}

/**
 * Generate the tiles produced by a container's terrain shapes.
 * Returns flat tile arrays [x, y, p, f, ...] per target layer id; palette indices refer to
 * doc.palette (entries may be appended).
 */
export function generateTerrain(doc: LevelDoc, container: TerrainContainer): Map<string, number[]> {
  const result = new Map<string, number[]>();
  if (!data || !container.terrains.length) return result;
  const res = generateContainer(container.terrains, (t) => {
    const { id, variant } = parseStyle(t.style, t.variant);
    const st = compiledStyle(id);
    if (!st) return null;
    const vi = Math.max(0, variant ? st.data.variants.findIndex((v) => v.name === variant) : 0);
    const key = `${id}|${vi}|${t.targets.level}|${t.targets.foreground ?? ''}|${t.targets.background ?? ''}`;
    return { key, style: st, variant: vi };
  }, data.samples, container.grassPaint, container.decoPaint, container.grassLines);
  const palKey = (p: PaletteEntry) => p.tex + '|' + p.sprite + '|' + (p.tile ?? '');
  const pal = new Map<string, number>();
  doc.palette.forEach((p, i) => {
    const k = palKey(p);
    if (!pal.has(k)) pal.set(k, i);
  });
  const layerIds = new Set(container.layers.map((l) => l.id));
  for (const [layerId, list] of res.tiles) {
    if (!layerIds.has(layerId)) continue;
    const cells = new Map<number, [number, number, number, number]>();
    for (const t of list) {
      const k = palKey(t.pe);
      let p = pal.get(k);
      if (p === undefined) {
        p = doc.palette.length;
        doc.palette.push(t.pe.tile ? { tex: t.pe.tex, sprite: t.pe.sprite, tile: t.pe.tile } : { tex: t.pe.tex, sprite: t.pe.sprite });
        pal.set(k, p);
      }
      cells.set((t.x + 32768) * 65536 + (t.y + 32768), [t.x, t.y, p, t.f]);
    }
    const arr: number[] = [];
    for (const c of cells.values()) arr.push(c[0], c[1], c[2], c[3]);
    result.set(layerId, arr);
  }
  missingOf.set(result, res.missing);
  surfaceOf.set(result, res.surface);
  surfaceStyles.set(res.surface, res.surfaceStyle);
  return result;
}

const missingOf = new WeakMap<Map<string, number[]>, MissingTile[]>();
const surfaceOf = new WeakMap<Map<string, number[]>, number[]>();
const surfaceStyles = new WeakMap<number[], string[]>();
const surfaceStyleMaps = new WeakMap<number[], Map<number, string>>();

/** Style of a surface cell of a generatedSurface result (null: not a surface cell). */
export function surfaceStyleAt(surface: number[], x: number, y: number): string | null {
  let m = surfaceStyleMaps.get(surface);
  if (!m) {
    m = new Map();
    const styles = surfaceStyles.get(surface) ?? [];
    for (let i = 0, j = 0; i + 4 < surface.length; i += 5, j++) if (styles[j] && !m.has(cellKey(surface[i], surface[i + 1]))) m.set(cellKey(surface[i], surface[i + 1]), styles[j]);
    surfaceStyleMaps.set(surface, m);
  }
  return m.get(cellKey(x, y)) ?? null;
}

/** Missing-tile cells found while producing a generateTerrain result. */
export function generatedMissing(out: Map<string, number[]>): MissingTile[] {
  return missingOf.get(out) ?? [];
}

/** Surface cells of a generateTerrain result (see GenerateOutput.surface). */
export function generatedSurface(out: Map<string, number[]>): number[] {
  return surfaceOf.get(out) ?? [];
}

// ------------------------------------------------------------------ tile cases (click to fix)

export interface CaseState {
  /** case key ('' when the layer has no case at the cell; BURIED for dirt under the surface near it) */
  key: string;
  /** dirt with no outline facing the air (see BURIED): only linkable for its exact neighbours */
  buried: boolean;
  /** hand-linked choices in effect (null: learned pieces are used) */
  linked: number[] | null;
  /** the link comes from the mirror image's entry */
  viaMirror: boolean;
  /** the case's link (caseLinked) is the general case's (inside corners with any shape of air around them) */
  viaGeneral: boolean;
  /** cases with inside corners: their general case and its link */
  general: { key: string; linked: number[] | null } | null;
  /** the case narrowed to exactly this cell's neighbours (see exactCase) and its own link */
  exact: { key: string; linked: number[] | null } | null;
  /** the link in effect is the exact-neighbours one */
  viaExact: boolean;
  /** the same neighbours, plus which of them show light ground (see toneCase) */
  tone: { key: string; linked: number[] | null } | null;
  /** the link in effect is the light/dark one */
  viaTone: boolean;
  /** the link the case has for any neighbours (what applies without an exact-neighbours link) */
  caseLinked: number[] | null;
  /** partly grassy cell without a tile of its own: the case's link (caseLinked) is of the case it falls back to */
  viaFallback: boolean;
  /** what the learned rules would put there */
  learned: number[];
}

export interface TerrainCellInfo {
  styleId: string;
  variant: number;
  x: number;
  y: number;
  own: number;
  nb: number[];
  /** final style choices at the cell (NONE: nothing) */
  level: number;
  fg: number;
  hasForeground: boolean;
  edge: boolean;
  edgeMode: EdgeArtMode;
  dirt: CaseState;
  /** the grass case is of the cell's real outline; partly grassy cells have their own (see partialCase) */
  grass: CaseState;
}

/**
 * Case of a cell and its links. `keys`: the case, then the cases it takes hand-linked tiles from without a
 * link of its own (partly grassy cells); `own` / `nb` the neighbourhood the learned pieces are chosen in.
 */
export function caseState(styleId: string, layer: CaseLayer, own: number, nb: number[], keys = [caseKey(layer, own, nb)], ring?: number[], light?: number[]): CaseState {
  const st = compiledStyle(styleId);
  if (!st) return { key: '', buried: false, linked: null, viaMirror: false, viaGeneral: false, general: null, exact: null, viaExact: false, tone: null, viaTone: false, caseLinked: null, viaFallback: false, learned: [] };
  let key = keys[0];
  let near: number[] = nb;
  let buried = false;
  if (layer === 'dirt' && !key && own === FULL && ring) {
    const bn = buriedNeighbours(nb, () => ring);
    if (bn) {
      key = BURIED;
      near = bn;
      buried = true;
    }
  }
  const xl = key ? linkedChoices(st, layer, key, near) : null;
  let l: ReturnType<typeof linkedChoices> = null;
  let at = 0;
  for (; at < keys.length && !l; at++) l = keys[at] ? linkedChoices(st, layer, keys[at]) : null;
  const exactHit = !!xl?.exact;
  const gk = generalCase(key);
  const general = gk ? { key: gk, linked: linkedChoices(st, layer, gk)?.arr ?? null } : null;
  const xk = exactCase(key, near);
  const exact = xk ? { key: xk, linked: st.cases[layer].has(xk) ? (linkedChoices(st, layer, xk)?.arr ?? null) : null } : null;
  let tone: CaseState['tone'] = null;
  let toneHit: ReturnType<typeof linkedChoices> = null;
  if (layer === 'dirt' && key && light) {
    let mask = 0;
    for (let i = 0; i < near.length; i++) if (light[i]) mask |= 1 << i;
    const tk = toneCase(key, near, mask);
    toneHit = linkedChoices(st, layer, tk);
    tone = { key: tk, linked: st.cases[layer].has(tk) ? (toneHit?.arr ?? null) : null };
  }
  const viaTone = !!toneHit;
  const shown = viaTone ? toneHit : exactHit ? xl : l;
  return {
    key,
    buried,
    linked: shown?.arr ?? null,
    viaMirror: !!shown?.mirrored,
    viaGeneral: !!l?.general,
    general,
    exact,
    viaExact: !viaTone && exactHit,
    tone,
    viaTone,
    caseLinked: l?.arr ?? null,
    viaFallback: !!l && at > 1,
    learned: key ? learnedChoices(st, layer, own, nb) : [],
  };
}

/** What the terrain of a container shows at cell (x, y) and which tile cases it uses there. */
export function terrainCellInfo(container: TerrainContainer, x: number, y: number): TerrainCellInfo | null {
  if (!data || !container.terrains.length) return null;
  const p = probeContainer(container.terrains, (t) => {
    const { id, variant } = parseStyle(t.style, t.variant);
    const st = compiledStyle(id);
    if (!st) return null;
    const vi = Math.max(0, variant ? st.data.variants.findIndex((v) => v.name === variant) : 0);
    return { key: id, style: st, variant: vi };
  }, x, y, data.samples, container.grassPaint, container.decoPaint, container.grassLines);
  if (!p) return null;
  const styleId = p.group;
  return {
    styleId,
    variant: p.variant,
    x,
    y,
    own: p.own,
    nb: p.nb,
    level: p.level,
    fg: p.fg,
    hasForeground: p.hasForeground,
    edge: p.edge,
    edgeMode: p.edgeMode,
    dirt: caseState(styleId, 'dirt', p.own, p.nb, undefined, p.ring, p.light),
    grass: caseState(styleId, 'grass', p.grassOwn, p.grassNb, p.grassKeys),
  };
}

const saveTimers = new Map<string, { timer: ReturnType<typeof setTimeout>; done: Promise<void>; resolve: () => void; reject: (e: unknown) => void }>();

/** Store a style's current override (debounced: rapid edits are saved once). */
export function saveStyleOverride(id: string, delay = 400): Promise<void> {
  let s = saveTimers.get(id);
  if (s) clearTimeout(s.timer);
  else {
    let resolve!: () => void, reject!: (e: unknown) => void;
    const done = new Promise<void>((a, b) => ((resolve = a), (reject = b)));
    s = { timer: 0 as unknown as ReturnType<typeof setTimeout>, done, resolve, reject };
    saveTimers.set(id, s);
  }
  const cur = s;
  cur.timer = setTimeout(() => {
    saveTimers.delete(id);
    saveAsset('terrain-style', id, overrides.get(id) ?? {}).then(cur.resolve, cur.reject);
  }, delay);
  return cur.done;
}

/** Edit a style's override in place (a copy is applied, generation updates immediately). */
export function editStyleOverride(id: string, fn: (o: StyleOverride) => void): StyleOverride {
  const n = structuredClone(overrides.get(id) ?? {});
  fn(n);
  setStyleOverride(id, n);
  return n;
}

/**
 * Style choice for a sprite (sprite index * 8 + flags), adding it to the style's sprite table if needed.
 * A sprite of another colour variant stands for its canonical-colour counterpart.
 */
export function styleChoiceFor(id: string, sprite: PaletteEntry, flags: number): number {
  const d = styleData(id);
  const st = compiledStyle(id);
  if (!d || !st) return NONE;
  const pe = canonicalSprite(st, sprite);
  const ov = overrides.get(id);
  const table = [...d.sprites, ...(ov?.extraSprites ?? [])];
  let si = table.findIndex((p) => p.tex === pe.tex && p.sprite === pe.sprite);
  if (si < 0) {
    si = table.length;
    editStyleOverride(id, (o) => {
      o.extraSprites = [...(o.extraSprites ?? []), pe.tile ? { tex: pe.tex, sprite: pe.sprite, tile: pe.tile } : { tex: pe.tex, sprite: pe.sprite }];
    });
  }
  return si * 8 + flags;
}

/** Link a tile case to choices (flat [choice, weight, ...]); null unlinks it, so the learned pieces are used again. Saved right away. */
export function linkCase(id: string, layer: CaseLayer, key: string, arr: number[] | null): Promise<void> {
  editStyleOverride(id, (o) => {
    const c = (o.cases ??= {});
    const t = (c[layer] ??= {});
    if (arr) t[key] = arr;
    else delete t[key];
  });
  return saveStyleOverride(id);
}

/** Problems of all shapes of a container as generated (unsupported cells after combining shapes). */
export function containerProblems(container: TerrainContainer): { x: number; y: number; message: string }[] {
  if (!data) return [];
  return generateContainer(container.terrains, (t) => {
    const { id } = parseStyle(t.style, t.variant);
    const st = compiledStyle(id);
    return st ? { key: id + '|' + t.targets.level, style: st, variant: 0 } : null;
  }, data.samples).problems;
}
